import * as PIXI from "pixi.js";
import chickenPng from "../assets/chicken.png";

class WebGLChicken {
  constructor(app) {
    this.app = app;
    this.container = new PIXI.Container();
    this.chickens = [];
    this.chickenTexture = null;
    this.chickenFrames = [];
    this.time = 0;
    this.init();
  }

  async init() {
    try {
      this.chickenTexture = await PIXI.Assets.load(chickenPng);
      this.createFrames();
      this.createChicken();
      this.updateBound = () => this.update();
      this.app.ticker.add(this.updateBound);
    } catch (error) {
      console.error("Failed to load chicken spritesheet:", error);
    }
  }

  createFrames() {
    if (!this.chickenTexture) return;

    // RPG spritesheet format: 3x4 grid (3 frames x 4 directions)
    // Each frame is 32x32 pixels
    // Standard RPG format: Row 0=North, Row 1=East, Row 2=South, Row 3=West
    const frameWidth = 32;
    const frameHeight = 32;

    // Create walking animation frames for all 4 directions
    this.walkingFrames = {
      north: [],
      east: [],
      south: [],
      west: [],
    };

    const directions = ["north", "east", "south", "west"];

    for (let row = 0; row < 4; row++) {
      const direction = directions[row];
      // Only use frames 0 and 2 for walking animation (skip center idle frame)
      for (let col of [0, 2]) {
        const frame = new PIXI.Texture({
          source: this.chickenTexture.source,
          frame: new PIXI.Rectangle(
            col * frameWidth,
            row * frameHeight,
            frameWidth,
            frameHeight
          ),
        });
        this.walkingFrames[direction].push(frame);
      }
    }

    // Start with south-facing frames (facing toward viewer)
    this.chickenFrames = [...this.walkingFrames.south];
  }

  createChicken() {
    if (!this.chickenFrames.length) return;

    // Start with south-facing frames
    const chicken = new PIXI.AnimatedSprite(this.walkingFrames.south);

    // Configure animation for natural leg alternating (2 frames)
    chicken.animationSpeed = 0.15;
    chicken.play();

    // Scale chicken to reasonable size (32x32 base size)
    const scale = 2;
    chicken.scale.set(scale);

    // Set anchor point to center
    chicken.anchor.set(0.5, 0.5);

    // Position randomly on screen, but keep 50px from bottom edge
    chicken.x = Math.random() * this.app.screen.width;
    chicken.y = Math.random() * (this.app.screen.height - 50);

    // Walking properties for 2D movement
    chicken.speed = 1.5;
    chicken.currentDirection = "south";
    chicken.vx = 0; // velocity x
    chicken.vy = chicken.speed; // velocity y (start walking down)

    // Timer for direction changes
    chicken.directionChangeTimer = 0;
    chicken.directionChangeDuration = 2000 + Math.random() * 3000; // 2-5 seconds

    this.chickens.push(chicken);
    this.container.addChild(chicken);
  }

  update() {
    const deltaTime = 16; // ~60fps
    this.time += deltaTime;

    this.chickens.forEach((chicken) => {
      // Update direction change timer
      chicken.directionChangeTimer += deltaTime;

      // Move chicken based on current velocity
      chicken.x += chicken.vx;
      chicken.y += chicken.vy;

      // Check for screen boundaries and bounce off or wrap around
      const margin = chicken.width / 2;
      let needsDirectionChange = false;

      if (chicken.x < margin || chicken.x > this.app.screen.width - margin) {
        // Hit left or right edge
        if (
          chicken.currentDirection === "east" ||
          chicken.currentDirection === "west"
        ) {
          needsDirectionChange = true;
        }
      }

      if (
        chicken.y < margin ||
        chicken.y > this.app.screen.height - 50 - margin
      ) {
        // Hit top or bottom edge (with 50px bottom margin)
        if (
          chicken.currentDirection === "north" ||
          chicken.currentDirection === "south"
        ) {
          needsDirectionChange = true;
        }
      }

      // Time-based random direction change
      if (chicken.directionChangeTimer >= chicken.directionChangeDuration) {
        needsDirectionChange = true;
        chicken.directionChangeTimer = 0;
        chicken.directionChangeDuration = 2000 + Math.random() * 3000; // 2-5 seconds
      }

      // Change direction if needed
      if (needsDirectionChange) {
        this.changeChickenDirection(chicken);
      }

      // Keep chicken within screen bounds, with 50px minimum from bottom
      chicken.x = Math.max(
        margin,
        Math.min(this.app.screen.width - margin, chicken.x)
      );
      chicken.y = Math.max(
        margin,
        Math.min(this.app.screen.height - 50 - margin, chicken.y)
      );
    });
  }

  changeChickenDirection(chicken) {
    const directions = ["north", "east", "south", "west"];

    // Pick a new random direction (avoid staying in same direction)
    let newDirection;
    do {
      newDirection = directions[Math.floor(Math.random() * directions.length)];
    } while (newDirection === chicken.currentDirection && Math.random() < 0.7); // 70% chance to change

    chicken.currentDirection = newDirection;

    // Update velocity based on new direction
    switch (newDirection) {
      case "north":
        chicken.vx = 0;
        chicken.vy = -chicken.speed;
        break;
      case "east":
        chicken.vx = chicken.speed;
        chicken.vy = 0;
        break;
      case "south":
        chicken.vx = 0;
        chicken.vy = chicken.speed;
        break;
      case "west":
        chicken.vx = -chicken.speed;
        chicken.vy = 0;
        break;
    }

    // Update sprite textures to match direction
    const newFrames = this.walkingFrames[newDirection];
    if (newFrames && newFrames.length > 0) {
      chicken.textures = newFrames;
      chicken.gotoAndPlay(0); // Restart animation with new frames
    }
  }

  destroy() {
    try {
      if (this.updateBound && this.app && this.app.ticker) {
        this.app.ticker.remove(this.updateBound);
        this.updateBound = null;
      }

      this.chickens = [];

      if (this.container) {
        this.container.destroy({ children: true, texture: false });
        this.container = null;
      }
    } catch (e) {
      console.warn("Error destroying WebGLChicken:", e);
    }
  }
}

export default WebGLChicken;
